import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux'
import {
    MarkerF,
    Circle,
    GoogleMap,
    OverlayView,
} from '@react-google-maps/api';

// Components
import GoogleMapsLoadScriptEnabler from "../components/GoogleMapsLoadScript";

// Constant
import { MAP } from '../../../../constants'

// Handler
import { MapInitialiserWithVehicleAndGeofence } from '../handlers/MapInitialiserWithVehicleAndGeofence'
import { geofenceTemplates_handler } from '../../../../handlers'
import { UnfocusMap } from '../handlers/UnfocusMap'

// Redux Action 
import * as ACTIONS from '../../../../services/redux/actions'

// Styles
import "../gmaps.css";

const {
    returnLatestGeofenceTemplateColorForGeoID
} = geofenceTemplates_handler

const GoogleMapsPOI = () => {

    const dispatch = useDispatch();

    // Redux Store
    const vehicles = useSelector(state => state.containers.v3.vehicles)
    const geofences = useSelector(state => state.containers.v3.geofences)
    const geofenceTemplates = useSelector(state => state.containers.v3.geofenceTemplates)
    const mapControl = useSelector(state => state.containers.v3.mapControl)

    // State
    const [mapRef, setMapRef] = useState(null);
    const [circleRef, setCircleRef] = useState(null);
    const [thisGeofence, setThisGeofence] = useState(mapControl.geofence);

    // Drawing mode state — replaces DrawingManager
    const [isDrawingMode, setIsDrawingMode] = useState(true);

    MapInitialiserWithVehicleAndGeofence(vehicles, geofences, mapControl, mapRef)

    const getMarker = (colorHexCode) => MAP.POI_MARKER_BY_COLOR[colorHexCode.replace("#", "").toUpperCase()]

    // Replaces DrawingManager's onMarkerComplete callback
    const handleMapClick = useCallback((e) => {
        if (!isDrawingMode) return;

        const lat = e.latLng.lat();
        const lng = e.latLng.lng();

        const newMarker = {
            ...mapControl.geofence,
            coordinates: [{
                lat: Number(lat),
                lng: Number(lng),
            }]
        };

        dispatch(ACTIONS.v3_mapControl.set_new_geofence_from_map(newMarker));

        // Setelah marker diletakkan, nonaktifkan drawing mode (sama seperti DrawingManager setDrawingMode(null))
        setIsDrawingMode(false);
    }, [isDrawingMode, mapControl.geofence, dispatch]);

    /**
     * Deep Cloning useEffect()
     */
    const useDeepEffect = (fn, deps) => {
        const isFirst = useRef(true);
        const prevDeps = useRef(deps);

        useEffect(() => {
            const isSame = prevDeps.current.every((obj, index) =>
                JSON.parse(JSON.stringify(obj)) === JSON.parse(JSON.stringify(deps[index]))
            );

            if (isFirst.current || !isSame) {
                fn();
            }

            isFirst.current = false;
            prevDeps.current = deps;
        }, [fn, deps]);
    }

    useDeepEffect(() => {
        setThisGeofence(mapControl.geofence)
    }, [mapControl.geofence])

    useEffect(() => {
        if (circleRef && mapRef) {
            const bounds = circleRef.getBounds()
            mapRef.fitBounds(bounds)
        }
    }, [mapControl, circleRef, mapRef])

    // Ubah cursor peta sesuai drawing mode
    useEffect(() => {
        if (mapRef) {
            mapRef.setOptions({
                draggableCursor: isDrawingMode ? 'crosshair' : null,
            });
        }
    }, [isDrawingMode, mapRef]);

    return (
        <div style={{ position: 'relative' }}>
            {/* Tombol Drawing Control — pengganti DrawingManager UI */}
            <div
                style={{
                    position: 'absolute',
                    top: 10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 10,
                    display: 'flex',
                    gap: 8,
                    background: 'white',
                    padding: '6px 10px',
                    borderRadius: 4,
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                }}
            >
                {/* Tombol Marker (meniru DrawingManager marker mode) */}
                <button
                    title="Tempatkan Marker"
                    onClick={() => setIsDrawingMode(true)}
                    style={{
                        padding: '4px 10px',
                        cursor: 'pointer',
                        background: isDrawingMode ? '#1a73e8' : '#fff',
                        color: isDrawingMode ? '#fff' : '#444',
                        border: '1px solid #ccc',
                        borderRadius: 4,
                        fontWeight: isDrawingMode ? 'bold' : 'normal',
                        fontSize: 13,
                    }}
                >
                    📍 Marker
                </button>

                {/* Tombol Pan/Drag mode */}
                <button
                    title="Mode Geser Peta"
                    onClick={() => setIsDrawingMode(false)}
                    style={{
                        padding: '4px 10px',
                        cursor: 'pointer',
                        background: !isDrawingMode ? '#1a73e8' : '#fff',
                        color: !isDrawingMode ? '#fff' : '#444',
                        border: '1px solid #ccc',
                        borderRadius: 4,
                        fontWeight: !isDrawingMode ? 'bold' : 'normal',
                        fontSize: 13,
                    }}
                >
                    ✋ Geser
                </button>
            </div>

            <GoogleMap
                id='googles-map'
                mapContainerStyle={{
                    height: '100vh'
                }}
                onLoad={ref => setMapRef(ref)}
                onClick={handleMapClick}
                onDragStart={() => { UnfocusMap(dispatch) }}
                onDragEnd={() => {
                    if (mapRef) {
                        const zoom = mapRef.getZoom();
                        const lat = mapRef.getCenter().lat();
                        const lng = mapRef.getCenter().lng();

                        mapRef.setCenter({ lat, lng });
                        mapRef.setZoom(zoom);
                    }
                }}
            >
                {/* POI Marker */}
                {
                    thisGeofence
                    && thisGeofence.coordinates &&
                    <MarkerF
                        icon={getMarker(returnLatestGeofenceTemplateColorForGeoID(geofenceTemplates, thisGeofence.geoid))}
                        position={thisGeofence.coordinates[0]}
                    >
                        <OverlayView
                            options={{ disableAutoPan: true }}
                            position={thisGeofence.coordinates[0]}
                            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        >
                            <div
                                style={{
                                    background: 'white',
                                    border: '1px solid #ccc',
                                    paddingLeft: 10,
                                    paddingRight: 10,
                                    paddingTop: 8,
                                    paddingBottom: 1,
                                }}
                            >
                                <h3>{thisGeofence.name}</h3>
                                {thisGeofence.comment && <>{thisGeofence.comment}</>}
                            </div>
                        </OverlayView>
                    </MarkerF>
                }

                {/* Geofence Circle */}
                {
                    thisGeofence
                    && thisGeofence.coordinates &&
                    <Circle
                        onLoad={ref => setCircleRef(ref)}
                        center={thisGeofence.coordinates[0]}
                        radius={thisGeofence.radius}
                        options={{
                            strokeColor: returnLatestGeofenceTemplateColorForGeoID(geofenceTemplates, thisGeofence.geoid)
                        }}
                    />
                }
            </GoogleMap>
        </div>
    )
}

export default GoogleMapsLoadScriptEnabler(GoogleMapsPOI)